/* istanbul ignore file - need to fix flakey tests */
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useModel } from '@partful/pages/instructions/viewer/lib/useModel';
import { useGoogleAnalytics } from '@partful/shared/lib/hooks/useGoogleAnalytics';
import { Button } from '@partful/shared/ui/Button';
import { useTenant } from '@providers/Tenant';
import { CategoryNode } from '@services/productInventory';
import { MaterialReactTable, MRT_TableOptions, useMaterialReactTable } from 'material-react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { ulid } from 'ulid';
import { usePartsCategories } from '../../lib/context/PartsCategoriesContext';
import { FlatNode } from '../../lib/hooks/useFlatTree';
import { usePartsCategoriesColumns } from '../../lib/hooks/usePartsCategoriesColumns';
import { useTree } from '../../lib/hooks/useTree';
import { useValidateNewCategory } from '../../lib/hooks/useValidateNewCategory';
import { MovePartDialog } from './MovePartDialog';
import styles from './PartsCategoriesDataTable.module.scss';
import { TableStateMessage } from './PartsCategoriesDataTableStateMessage';
import { PartsCategoriesTableRowActions } from './PartsCategorieTableRowActions';
import { SavePartsCategories } from './SavePartsCategories';

const isTest = process.env.NODE_ENV === 'test';

export function PartsCategoriesDataTable() {
  const { t: i18n } = useTranslation('manageProduct');
  const { state, addCategory, updateCategory, moveCategory } = usePartsCategories();

  const { sendEvent } = useGoogleAnalytics();

  const treeData = useTree<FlatNode<CategoryNode>>(state);

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [editingRowName, setEditingRowName] = useState<string>('');
  const [selectedRowsToMove, setSelectedRowsToMove] = useState<CategoryNode[]>([]);

  const { validateRow } = useValidateNewCategory(state);

  const handleValueChange = (value: { [key: string]: string }) => {
    setEditingRowName(value.name);
  };

  const { accountId: account } = useTenant();
  const { productId: product } = useParams<{ productId: string }>();
  const [{ data: model }] = useModel({ account, product });

  const columns = usePartsCategoriesColumns<CategoryNode>({
    validationErrors,
    onValueChange: handleValueChange,
    model,
  });

  const handleAddCategory = () => {
    table.setCreatingRow(true);
    setValidationErrors({});
    setEditingRowName('');
    sendEvent('pcui_create_category');
  };

  const handleSaveCreatedRow = () => {
    const newCategory: CategoryNode = {
      PID: ulid(),
      name: editingRowName,
      partNumber: '',
      itemType: 'Category',
      isCategory: true,
      children: [],
    };
    const errors = validateRow(newCategory);

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    addCategory(editingRowName);

    table.setCreatingRow(null);
    sendEvent('pcui_create_category_save');
  };

  const handleCancelRow = () => {
    setEditingRowName('');
    sendEvent('pcui_create_category_cancel');
  };

  const handleUpdateCategory: MRT_TableOptions<CategoryNode>['onEditingRowSave'] = ({ exitEditingMode, row }) => {
    const editingRow: CategoryNode = { ...row.original, name: editingRowName };
    const errors = validateRow(editingRow);

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    updateCategory(editingRow);
    exitEditingMode();
    sendEvent('pcui_edit_category_save');
  };

  const onMoveNode = (targetNode: CategoryNode) => {
    if (selectedRowsToMove.length) {
      moveCategory(selectedRowsToMove, targetNode);
      setSelectedRowsToMove([]);
      table.resetRowSelection();

      if (selectedRowsToMove.length > 1) {
        sendEvent('pcui_move_multiple_to');
      }

      sendEvent('pcui_move_to');
    }
  };

  const onCancelMove = () => {
    setSelectedRowsToMove([]);

    sendEvent('pcui_move_to_cancel');
  };

  const isLoading = state.length === 0;

  const table = useMaterialReactTable({
    data: treeData,
    getSubRows: (originalRow) => originalRow.children,
    getRowId: (row) => row.PID,
    columns,
    positionCreatingRow: 0,
    enableEditing: true,
    createDisplayMode: 'row',
    onCreatingRowSave: handleSaveCreatedRow,
    onCreatingRowCancel: handleCancelRow,
    editDisplayMode: 'row',
    onEditingRowSave: handleUpdateCategory,
    enableRowSelection: true,
    enableExpanding: true,
    // Virtualization breaks the tests rendering children in tbody when set to true...
    enableRowVirtualization: !isTest,
    enableBottomToolbar: false,
    enableColumnResizing: false,
    enableColumnActions: false,
    enableRowNumbers: false,
    enablePagination: false,
    enableSorting: false,
    enableTopToolbar: false,
    enableSelectAll: true,
    enableExpandAll: false,
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ table, row }) => (
      <PartsCategoriesTableRowActions
        table={table}
        row={row}
        onSelectRowsToMove={(rows) => setSelectedRowsToMove(rows)}
      />
    ),
    state: { isLoading, showSkeletons: false, showProgressBars: false },
    displayColumnDefOptions: {
      'mrt-row-expand': {
        grow: true,
        header: 'Hierarchy',
      },
      'mrt-row-select': {
        header: '',
      },
      'mrt-row-actions': {
        header: '',
        size: 100,
      },
    },
    muiCircularProgressProps: {
      Component: <TableStateMessage state="loading" />,
    },
    muiExpandButtonProps: ({ row }) => ({
      className: styles.expandIcon,
      'aria-selected': row.getIsSelected(),
      'data-can-expand': row.original.children?.length > 0,
      'data-testid': 'row-expand-btn',
      sx: {
        display: row.id === 'mrt-row-create' ? 'none' : 'block',
      },
    }),
    muiSelectCheckboxProps: ({ row }) => ({
      className: styles.selectCheckbox,
      'aria-selected': row.getIsSelected(),
      sx: {
        display: row.id === 'mrt-row-create' ? 'none' : 'block',
      },
    }),
    muiSelectAllCheckboxProps: ({ table }) => ({
      className: styles.selectAllCheckbox,
      'aria-selected': table.getIsAllRowsSelected(),
    }),
    muiTableHeadRowProps: {
      className: styles.tableHeadRow,
    },
    muiTableHeadCellProps: {
      className: styles.tableHeadCell,
    },
    muiTableBodyRowProps: /* istanbul ignore next - ignoring as checking for the values
     * in the table's dom element doesn't satisfy jests coverage rule */ ({ row }) => {
      const parentRows = row.getParentRows();
      const isAnyParentExpanded = parentRows?.some((row) => row.getIsExpanded());

      const isRowExpanded = row.getIsExpanded();
      const isRowSelected = row.getIsSelected();

      const isThisRowTheFirstExpandedRow = !row.parentId && parentRows.length === 0 && isRowExpanded;
      const isThisRowTheLastExpandedRow = parentRows?.[0]?.getLeafRows().length === row.index + 1;

      return {
        className: styles.tableBodyRow,
        'aria-selected': isRowSelected,
        'aria-expanded': isRowExpanded || isAnyParentExpanded,
        'data-is-first-expanded-row': isThisRowTheFirstExpandedRow,
        'data-is-last-expanded-row': isThisRowTheLastExpandedRow,
      };
    },
    muiTableBodyCellProps: ({ row }) => ({
      className: styles.tableBodyCell,
      'aria-selected': row.getIsSelected(),
    }),
  });

  const isEditingOrCreatingInline = table.getState().creatingRow !== null || table.getState().editingRow !== null;

  return (
    <>
      <div>
        <Button size="large" onClick={handleAddCategory} disabled={isEditingOrCreatingInline}>
          <FontAwesomeIcon icon={faPlus} size="xl" />
          {i18n('partsCategories.interactive.createCategory')}
        </Button>
      </div>
      <MaterialReactTable table={table} />
      {!isLoading && (
        <SavePartsCategories
          selectedRows={table.getSelectedRowModel().flatRows}
          onSelectRowsToMove={(rows) => setSelectedRowsToMove(rows)}
          resetRowSelection={() => table.resetRowSelection()}
        />
      )}
      <MovePartDialog
        selectedRowsToMove={selectedRowsToMove}
        data={state}
        onMovePart={onMoveNode}
        onCancel={onCancelMove}
      />
    </>
  );
}
